import { FormattedMessage } from 'react-intl';
import { FormikPoolDatePicker } from '@synergy/components-next';
import { PaloField } from '@synergy/components-palo';
import { useFormikContext } from 'formik';
import { useRef, useState } from 'react';

import { OverviewFormValues, overviewFieldNames } from '../../../models/overview-form-values';
import { PaloFormikField } from '../../../../../../common/components/palo/palo-formik-field';
import { PaloInvoiceSupplierLock } from '../../../../../../common/components/palo/palo-locked-badge';
import { PaloSupplierChangeDialog } from '../../../../../../common/components/palo/palo-supplier-change-dialog';
import { PaloTextControl } from '../../../../../../common/components/palo/palo-text-control';
import {
    PartialAccountContact,
    PoolAccountContactAutocompleteList,
} from '../../../../../../common/components/autocomplete-list/account-contact-autocomplete-list/pool-account-contact-autocomplete-list';
import {
    PartialSupplier,
    PoolSupplierAutocompleteList,
} from '../../../../../../common/components/autocomplete-list/supplier-autocomplete-list/pool-supplier-autocomplete-list';
import { PaymentTermAutocompleteRef } from '../../../../../../common/components/payment-term-autocomplete/payment-term-autocomplete';
import { Supplier } from '../../../../../../common/models/supplier';
import { useInvoiceDetailContext } from '../../../../context/invoice-detail-context';

interface PaloInvoiceHeadDeliveryProps extends Pick<PaymentTermAutocompleteRef, 'getPaymentTerm'> {
    readonly?: boolean;
}

/** Who delivered, and the supplier's own numbers for it. */
export const PaloInvoiceHeadDelivery = (props: PaloInvoiceHeadDeliveryProps) => {
    const { readonly, getPaymentTerm } = props;
    const formik = useFormikContext<OverviewFormValues>();
    const { positionCount, removeAllPositions } = useInvoiceDetailContext();
    /**
     * A supplier picked while positions are already on the document waits
     * here until it is clear what happens to them (Prüfbericht 28.09.2026).
     */
    const [pendingSupplier, setPendingSupplier] = useState<{ supplier: Supplier | null } | null>(null);
    /**
     * The name of the supplier the document had. Clearing the field empties
     * the value before the change is reported, so the question that follows
     * could no longer say whom the positions came from.
     */
    const lastSupplierName = useRef<string | undefined>(undefined);

    if (formik.values.supplierAccount?.name) {
        lastSupplierName.current = formik.values.supplierAccount.name;
    }

    const applySupplier = async (supplier: Supplier | null) => {
        const nextValues = {
            ...formik.values,
            supplierAccount: supplier,
            supplierContact: null,
            functionalAsset: null,
        };

        if (supplier?.paymentTermId && getPaymentTerm) {
            nextValues.paymentTerm = getPaymentTerm(supplier.paymentTermId);
        } else if (formik.values.recipientAccount?.paymentTermId && getPaymentTerm) {
            nextValues.paymentTerm = getPaymentTerm(formik.values.recipientAccount.paymentTermId);
        } else {
            nextValues.paymentTerm = null;
        }
        await formik.setValues(nextValues);
    };

    const handleSupplierSelected = async (supplier: Supplier | null) => {
        const current = formik.values.supplierAccount?.id;

        // The first supplier, nothing picked yet, or the same one again: no
        // question to ask — there is nothing that could be left orphaned.
        if (!current || 0 === positionCount || current === supplier?.id) {
            await applySupplier(supplier);

            return;
        }

        setPendingSupplier({ supplier });
    };

    return (
        <>
            <PaloSupplierChangeDialog
                open={null !== pendingSupplier}
                count={positionCount}
                previousSupplier={formik.values.supplierAccount?.name ?? lastSupplierName.current}
                onCancel={() => setPendingSupplier(null)}
                onKeep={async () => {
                    const picked = pendingSupplier;

                    setPendingSupplier(null);
                    await applySupplier(picked?.supplier ?? null);
                }}
                onRemove={async () => {
                    const picked = pendingSupplier;

                    setPendingSupplier(null);
                    removeAllPositions?.();
                    await applySupplier(picked?.supplier ?? null);
                }}
            />
            <PaloFormikField<PartialSupplier>
                name={overviewFieldNames.supplierAccount}
                label={<FormattedMessage id="app.label.supplierAccount" />}
                required
                badge={<PaloInvoiceSupplierLock />}
                area="supplier"
            >
                {({ field, hasError }) => (
                    <PoolSupplierAutocompleteList
                        label={<></>}
                        required={false}
                        onSelected={handleSupplierSelected}
                        error={hasError}
                        readOnly={readonly}
                        isLocked={false}
                        {...field}
                    />
                )}
            </PaloFormikField>
            <PaloFormikField<PartialAccountContact>
                name={overviewFieldNames.supplierContact}
                label={<FormattedMessage id="app.label.supplierContactFull" />}
                area="supplierContact"
            >
                {({ field, hasError }) => (
                    <PoolAccountContactAutocompleteList
                        accountId={formik.values.supplierAccount?.id}
                        accountType="supplier"
                        label={<></>}
                        onSelected={value => formik.setFieldValue(field.name, value)}
                        error={hasError}
                        disabled={!formik.values.supplierAccount}
                        readOnly={readonly}
                        {...field}
                    />
                )}
            </PaloFormikField>
            <PaloField
                label={<FormattedMessage id="app.label.deliveryDate" />}
                name={overviewFieldNames.deliveryDate}
                area="deliveryDate"
            >
                <FormikPoolDatePicker
                    name={overviewFieldNames.deliveryDate}
                    setTimeToMidday={true}
                    readOnly={readonly}
                />
            </PaloField>
            <PaloTextControl
                name={overviewFieldNames.incomingInvoiceNumber}
                label={<FormattedMessage id="app.label.incomingInvoiceNumber" />}
                area="incomingInvoiceNumber"
                disabled={readonly}
            />
            <PaloTextControl
                name={overviewFieldNames.deliveryNumber}
                label={<FormattedMessage id="app.label.deliveryNumber" />}
                area="deliveryNumber"
                disabled={readonly}
            />
        </>
    );
};
